/**
 * walk-scene entry point — multi-scene orchestration.
 *
 * Boot fetches a runtime registry (`/assets/scenes/registry.json`) and loads the
 * entry scene via SceneManager. `switchTo(sceneId, spawn, angles)` drives every
 * scene transition: it fades out, unloads the current scene (freeing its gsplat
 * VRAM), loads the new manifest + content, re-binds the UI, re-seats the player
 * (new collision / spawn / mode), and fades back in. Walking into a portal emits
 * PORTAL_ENTER, which calls switchTo with the portal's target scene; the target
 * scene manifest owns the spawn point unless a caller explicitly overrides it.
 *
 * The single update loop drives input → active controller → rig → managers, but
 * is gated while a switch is in progress so the player can't move mid-transition.
 */

import './styles.css';
import { bootstrapBusiness } from './platform/pages';
import { RoomSession } from './network/RoomSession';
import { RemotePlayers, modelConfig } from './network/RemotePlayers';
import { classifyLocomotion } from './network/locomotion';
import type { Admission, PlayerState, ActionState, Avatar } from '../../../vrroom_typescript/src/platform/contracts';
import { loadCollision } from './scene/loadCollision';

import { Vec3, GSPLAT_DEBUG_NONE, GSPLAT_DEBUG_LOD, GSPLAT_DEBUG_NODE_AABBS } from 'playcanvas';

import { createApp, isMobileRuntime } from './app/createApp';
import { globalConfig } from './config/global.config';
import { fetchRegistry, fetchSceneManifest } from './config/manifest';
import type { SceneManifest } from './config/manifest';
import { eventBus } from './events/EventBus';
import { InputController } from './input/input-controller';
import { PointerLockManager } from './input/pointer-lock';
import { CameraState } from './player/types';
import { FirstPersonController } from './player/FirstPersonController';
import { CharacterController } from './player/CharacterController';
import { PlayerRig } from './player/PlayerRig';
import { ThirdPersonController } from './player/ThirdPersonController';
import { AvatarController } from './player/AvatarController';
import { AnimationStateMachine } from './player/AnimationStateMachine';
import { avatarConfig } from './config/avatar.config';
import type { CameraController } from './player/types';
import { SceneManager } from './scene/SceneManager';
import { preloadGaussianScene } from './scene/loadGaussianScene';
import { SceneEditor } from './scene/SceneEditor';
import { EditorToolbar } from './ui/EditorToolbar';
import { EditorAssetLibrary } from './editor/EditorAssetLibrary';
import { UiManager } from './ui/UiManager';
import { WorldUI } from './ui/WorldUI';
import { HudControls } from './ui/HudControls';
import type { Collision } from './collision';

function isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) return false;
    const tag = target.tagName;
    return target.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

type RenderGateApp = { renderNextFrame?: boolean };

const MOBILE_RENDER_IDLE_SECONDS = 4;
const MOBILE_RENDER_POS_EPS_SQ = 0.00001;
const MOBILE_RENDER_ANGLE_EPS_SQ = 0.0001;
const TOUCH_TAP_MOVE_THRESHOLD = 10;
const TOUCH_TAP_MAX_MS = 350;
// Mobile background near-field gsplat sharpening (see switchTo). Deliberately
// bounded: small radius + LOD-capped, unlike the old mobile 'full' background
// preload that swept every coarsest chunk and OOM-killed meihuashan.
const MOBILE_NEAR_PRELOAD_DELAY_MS = 650;  // after collision/occlusion kick off, alongside skybox
const MOBILE_NEAR_PRELOAD_LOD = 3;         // LOD3 ≈ 20% density ≈ what the 3M mobile budget selects near-field
const MOBILE_NEAR_PRELOAD_RADIUS_M = 12;

async function main() {
    const business = await bootstrapBusiness();
    if (!business) return;
    const session = new RoomSession(business.user.id, business.invite);
    // 世界场景（/solo）= 离线单人漫游：RoomSession 保持未连接（publish/command
    // 全部 no-op），不进入房间也不同步其他玩家；与「加入场景」的联机房间分开。
    const solo = business.solo === true;
    const canvas = document.getElementById('app') as HTMLCanvasElement;
    const { app, cameraRoot, camera } = await createApp(canvas);

    const ui = new UiManager();
    const sceneManager = new SceneManager(app);
    const remotes = new RemotePlayers(app, camera, business.user.id,
        // Local body used for nameplate occlusion: hidden avatars (first person /
        // camera pulled in) must not block remote nameplates.
        () => avatar.visible ? { position: rig.root.getPosition(), feetDrop: avatar.feetDrop } : null);
    session.onSnapshot = snapshot => { remotes.snapshot(snapshot); const me = snapshot.players.find(p => p.profile.id === business.user.id); if (me) acceptAction(me.action, me.state.membershipEpoch); };
    session.onStates = batch => remotes.states(batch);
    session.onEvent = (name, data) => { remotes.event(name, data); if (name === 'player:action' && data.userId === business.user.id) acceptAction(data.action, data.membershipEpoch); if (name === 'room:kicked' || name === 'room:closed') remotes.clear(); };
    // 房间/传送状态（含「传送未完成：PORTAL_UNCONFIGURED」等失败原因）此前无人
    // 订阅而完全静默，绑定到 HUD hint 让玩家能看到传送为什么失败。
    session.onStatus = (message) => ui.showHint(message);
    // Release pointer lock whenever a popup opens so the mouse cursor is free
    // to click the close button.
    ui.requestUnlock = () => pointerLock.exit();

    // Pause all in-world scene videos when any popup (panel/media) opens, and
    // restore their prior play/pause state when the popup closes. Handles
    // multiple video screens: tracks which IDs were paused so the right ones
    // are resumed.
    let savedVideoIds: string[] = [];
    ui.onPopupOpen = () => {
        savedVideoIds = sceneManager.current?.objects.pauseAllVideoScreens() ?? [];
    };
    ui.onPopupClose = () => {
        if (savedVideoIds.length) {
            sceneManager.current?.objects.resumeVideoScreens(savedVideoIds);
            savedVideoIds = [];
        }
    };

    // In-scene hub menu (⚙ 设置): 世界/创建/加入/角色/分享/公告/帮助/反馈,
    // backed by the authenticated /api/v1/hub endpoints. Room switching hands
    // control back to the standard join flow via a full page navigation.
    const worldUI = new WorldUI(ui, {
        user: business.user,
        api: business.api,
        currentRoom: business.invite,
        onJoinRoom: (target) => {
            location.assign('/join?' + new URLSearchParams({
                roomId: target.roomId, sceneId: target.sceneId, invite: target.invite
            }));
        },
        // 世界场景点击卡片 → 离线单人漫游（独立 /solo 入口，不进联机房间）。
        onEnterScene: (sceneId) => {
            location.assign('/solo?' + new URLSearchParams({ sceneId }));
        },
        // 角色面板切换形象 → 立即重建本地第三人称模型（无需重进场景）
        onAvatarChange: (avatarId) => {
            void business.api<{ id: string; modelUrl: string; config: Record<string, unknown> }[]>('/hub/avatars')
                .then(async (list) => {
                    const found = list.find((a) => a.id === avatarId);
                    if (found) await applyAvatar({ ...found, revision: 1, enabled: true } as Avatar);
                })
                .catch(() => ui.showHint('新形象加载失败'));
        }
    });

    // --- Persistent player rig + controllers (reused across scenes) ---
    const rig = new PlayerRig(cameraRoot, camera);
    const fly = new FirstPersonController();
    const walk = new CharacterController();
    const third = new ThirdPersonController();
    // Avatar loads asynchronously; a placeholder capsule is used if the GLB is
    // absent, so the third-person system is testable before player.glb exists.
    let avatar = new AvatarController(app, rig, avatarConfig);
    avatar.setVisible(false); // first-person by default; shown when entering third-person.
    void avatar.load();
    let anim = new AnimationStateMachine(avatar);
    let localAction: ActionState | null = null;
    let actionEpoch = -1;
    let renderedActionSeq = -1;
    function acceptAction(action: ActionState, epoch: number) {
        if (epoch !== session.admission?.membershipEpoch) return;
        if (epoch === actionEpoch && localAction && action.actionSeq < localAction.actionSeq) return;
        if (epoch !== actionEpoch) renderedActionSeq = -1;
        actionEpoch = epoch; localAction = action;
    }
    function updateNetworkAction() {
        // 离线单人漫游：动作由 HUD 本地播放（AnimationStateMachine 自带打断/
        // 播完回 idle 逻辑），不走网络状态机，只需维持动作期间的 root-motion 锁。
        if (solo) {
            if (anim.manualActionActive) avatar.lockRootMotion('action', anim.manualActionName);
            return;
        }
        const action = localAction;
        const elapsed = action ? session.serverTime - action.startedAt : 0;
        if (!action?.actionId || actionEpoch !== session.admission?.membershipEpoch || (!action.loop && elapsed >= action.duration)) {
            if (anim.manualActionActive) anim.reset(); return;
        }
        if (elapsed < 0) return;
        if (renderedActionSeq !== action.actionSeq || anim.manualActionName !== action.actionId) {
            if (!anim.playAction(action.actionId)) return;
            const layer = avatar.animLayer, duration = layer?.activeStateDuration ?? 0;
            if (layer && duration > 0) layer.activeStateCurrentTime = action.loop ? elapsed / 1000 % duration : Math.min(duration * 0.999, elapsed / 1000);
            renderedActionSeq = action.actionSeq;
        }
        avatar.lockRootMotion('action', action.actionId);
    }
    third.avatar = avatar;
    third.anim = anim;
    const state = new CameraState();
    state.fov = globalConfig.fov;

    let active: CameraController = fly;
    let currentCollision: Collision | null = null;
    /** 'first' = fly/walk first-person; 'third' = orbiting follow camera. */
    let viewMode: 'first' | 'third' = 'first';
    /** Movement semantics shared by first- and third-person views. */
    let moveMode: 'fly' | 'walk' = 'fly';

    // --- Input + pointer lock ---
    const input = new InputController(canvas);
    input.mouseSensitivity = globalConfig.mouseSensitivity;
    const pointerLock = new PointerLockManager(canvas);
    const crosshair = document.getElementById('crosshair');
    if (crosshair) crosshair.classList.add('reticle-hidden');
    const fpsMeter = document.getElementById('fps-meter');
    const renderGateApp = app as unknown as RenderGateApp;
    const lastRenderState = new CameraState();
    let lastRenderStateReady = false;
    let mobileRenderIdleTime = 0;
    let fpsAccum = 0;
    let fpsFrames = 0;

    // --- Mouse cursor tracking for click interactions (non-editor mode) ---
    // In game mode, clicks use the mouse cursor position (not screen center)
    // to build a world-space ray for hotspot/object picking.
    let _mouseX = 0;
    let _mouseY = 0;
    let _mouseValid = false;
    window.addEventListener('mousemove', (e) => {
        _mouseX = e.clientX;
        _mouseY = e.clientY;
        _mouseValid = true;
    }, { passive: true });

    async function requestMobileLandscape() {
        try {
            await document.documentElement.requestFullscreen?.();
            const orientation = screen.orientation as ScreenOrientation & { lock?: (orientation: 'landscape') => Promise<void> };
            await orientation.lock?.('landscape');
        } catch (err) {
            console.warn('[mobile] landscape lock unavailable:', err);
        }
    }
    window.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') void requestMobileLandscape();
    }, { once: true, passive: true });

    function triggerSceneClickInteraction(): boolean {
        // Use mouse/touch position to build a world-space ray (instead of
        // screen-center / crosshair forward). Falls back to camera forward
        // when no mouse/touch position has been recorded yet.
        // On desktop with pointer lock active, clientX/clientY are frozen, so
        // use camera forward (center of screen) instead of stale mouse coords.
        if (!_mouseValid || pointerLock.locked) {
            const hotspotHit = sceneManager.current?.hotspots.onClick(state) ?? null;
            const objectHit = sceneManager.current?.objects.onClick(state) ?? null;
            return Boolean(hotspotHit || objectHit);
        }
        // PlayCanvas screenToWorld uses canvas.getBoundingClientRect() internally,
        // which returns the visual bounding box (already accounts for CSS transforms
        // like the mobile virtual-landscape rotate). So we can pass raw clientX/clientY.
        const origin = camera.getWorldTransform().getTranslation();
        const far = new Vec3();
        camera.camera!.screenToWorld(_mouseX, _mouseY, 1000, far);
        const dir = new Vec3(far.x - origin.x, far.y - origin.y, far.z - origin.z).normalize();
        const hotspotHit = sceneManager.current?.hotspots.onClickRay(origin.x, origin.y, origin.z, dir.x, dir.y, dir.z) ?? null;
        const objectHit = sceneManager.current?.objects.onClickRay(origin.x, origin.y, origin.z, dir.x, dir.y, dir.z) ?? null;
        return Boolean(hotspotHit || objectHit);
    }

    // Fixed bottom-right gameplay controls. Hidden in editor.
    const hud = new HudControls(input, {
        onToggleView: () => toggleThirdPerson(),
        onToggleMoveMode: () => toggleMoveMode(),
        onAvatarAction: (name) => {
            if (!session.online) {
                // 离线单人漫游：动作直接本地播放；联机房间未就绪时忽略。
                if (solo) anim.playAction(name);
                return;
            }
            const epoch = session.admission!.membershipEpoch;
            void session.action(name).then(action => acceptAction(action, epoch)).catch(e => ui.showHint(e.message));
        },
        onVideoShow: () => sceneManager.current?.objects.showFocusedVideoScreen(state),
        onVideoHide: () => sceneManager.current?.objects.hideFocusedVideoScreen(state),
        onVideoPlayPause: () => sceneManager.current?.objects.toggleVideoPlayback(state),
        onVideoMuteToggle: () => sceneManager.current?.objects.toggleVideoMuted(state),
        onVideoSeek: (time) => sceneManager.current?.objects.seekVideo(state, time),
        getViewMode: () => viewMode,
        getMoveMode: () => moveMode,
        getVideoState: () => sceneManager.current?.objects.getVideoControlState(state) ?? null,
        actionAnims: avatarConfig.actionAnims
    });
    // HUD 构造时即为 hidden；场景就绪（switchTo 内）后才显示，
    // 登录/加载阶段不出现任何玩法按钮。

    // --- Manifest cache + registry ---
    const manifestCache = new Map<string, SceneManifest>();
    let registry = await fetchRegistry(globalConfig.registryUrl);
    const sceneIds = () => registry.scenes.map((s) => s.id);

    async function getManifest(sceneId: string): Promise<SceneManifest> {
        const cached = manifestCache.get(sceneId);
        if (cached) return cached;
        const manifest = await fetchSceneManifest(`/assets/scenes/${sceneId}/scene.json`);
        manifestCache.set(sceneId, manifest);
        return manifest;
    }

    // --- Editor (Scene Builder): place/select/edit unified objects, export JSON ---
    const assetLib = new EditorAssetLibrary();
    await assetLib.load();
    const editor = new SceneEditor(app, sceneManager, camera, state, sceneIds, assetLib, (t) => ui.showHint(t));
    // Save: persist via the editor, then drop the cached manifest so the next
    // visit (or a browser refresh) re-reads the freshly written scene.json.
    const saveCurrent = async () => {
        const msg = await editor.save();
        const id = sceneManager.current?.manifest.id;
        if (id) manifestCache.delete(id);
        // Surface the save outcome prominently so it's unmissable: a successful
        // PUT, a download fallback, or a validation failure each read differently.
        if (msg) ui.setStatus(msg);
    };

    const toolbar = new EditorToolbar(
        editor.state,
        assetLib,
        (assetId) => editor.beginPlace(assetId),
        async () => {
            editor.setSpawnFromCurrentPose();
            await saveCurrent();
        },
        saveCurrent,
        () => editor.export(),
        () => exitEditor()
    );
    editor.attachPanels(toolbar.propertyMount, toolbar.listMount);
    editor.onSelectChange = (id) => toolbar.setPlacingActive(id);

    // Snapshot of the movement/view mode taken when entering the editor, so
    // exitEditor() can restore it (enterEditor forces first-person fly).
    let preEditorViewMode: 'first' | 'third' | null = null;
    let preEditorMoveMode: 'fly' | 'walk' | null = null;
    // Saved fly altitude clamps; disabled while the editor is active so the
    // designer can free-fly to any height when placing objects.
    let preEditorFlyMaxHeight = 0;
    let preEditorThirdMaxHeight = 0;

    function enterEditor() {
        ui.showHint('多人活动中不能编辑场景；请在管理后台配置空闲场景。');
        return;
        /* Offline editor retained as a source tool; room scenes are server-owned. */
        if (editor.enabled) return;
        editor.toggle();
        toolbar.show();
        hud.setVisible(true);
        // Editor is cursor-driven: release the pointer lock so the cursor is
        // visible and the designer can click the toolbar/panels.
        if (pointerLock.locked) pointerLock.exit();
        // Force fly mode while editing so the designer can free-fly around the
        // scene to place/select objects without walk-mode collision constraints.
        // Snapshot the current mode first so exitEditor() can restore it.
        preEditorViewMode = viewMode;
        preEditorMoveMode = moveMode;
        setMode('fly');
        // Disable the fly altitude clamp while editing so the designer can
        // free-fly to any height.
        preEditorFlyMaxHeight = fly.flyMaxHeight;
        preEditorThirdMaxHeight = third.flyMaxHeight;
        fly.flyMaxHeight = Infinity;
        third.flyMaxHeight = Infinity;
    }
    function exitEditor() {
        if (!editor.enabled) return;
        editor.toggle();
        toolbar.hide();
        hud.setVisible(false);
        // Restore the movement/view mode that was active before entering the
        // editor (enterEditor forces first-person fly).
        if (preEditorViewMode === 'third') {
            moveMode = preEditorMoveMode ?? 'fly';
            syncMoveMode();
            if (viewMode !== 'third') toggleThirdPerson();
        } else if (preEditorMoveMode) {
            setMode(preEditorMoveMode);
        }
        preEditorViewMode = null;
        preEditorMoveMode = null;
        // Restore the fly altitude clamp that was disabled in enterEditor().
        fly.flyMaxHeight = preEditorFlyMaxHeight;
        third.flyMaxHeight = preEditorThirdMaxHeight;
    }

    // --- Switching guard: true while a scene transition is in flight ---
    let switching = false;

    function syncMoveMode(): void {
        input.mode = moveMode;
        third.moveMode = moveMode;
    }

    function emitModeChange(): void {
        eventBus.emit({
            type: 'MODE_CHANGE',
            mode: viewMode === 'third' ? 'third' : moveMode,
            viewMode,
            moveMode
        });
    }

    function firstPersonController(): CameraController {
        return moveMode === 'walk' ? walk : fly;
    }

    /** Re-seat the player into the just-loaded scene. */
    function applySceneToPlayer(
        manifest: SceneManifest,
        collision: Collision | null,
        spawnOverride?: [number, number, number],
        anglesOverride?: [number, number]
    ) {
        currentCollision = collision;
        fly.collision = collision;
        walk.collision = collision;
        walk.fallLimit = manifest.fallLimit;
        third.collision = collision;
        third.fallLimit = manifest.fallLimit;

        const spawn = spawnOverride ?? manifest.spawn;
        const angles = anglesOverride ?? manifest.spawnAngles;
        state.position.set(spawn[0], spawn[1], spawn[2]);
        state.angles.set(angles[0], angles[1], 0);
        state.cameraOffset.set(0, 0, 0);
        state.fov = globalConfig.fov;

        // Default movement mode for this scene (walk needs collision).
        const wantWalk = collision != null && manifest.defaultMode === 'walk';
        moveMode = wantWalk ? 'walk' : 'fly';
        syncMoveMode();

        // Re-activate the controller matching the current view. Third-person
        // fly can run without collision; third-person walk falls back if needed.
        if (viewMode === 'third' && (moveMode === 'fly' || collision != null)) {
            active = third;
            avatar.setVisible(true);
        } else {
            viewMode = 'first';
            active = firstPersonController();
            avatar.setVisible(false);
            avatar.clearFlightPose();
            state.cameraOffset.set(0, 0, 0);
        }
        active.onEnter(state);
        emitModeChange();
        hud.sync();
    }

    function installSceneCollision(collision: Collision | null): void {
        const hadCollision = currentCollision !== null;
        currentCollision = collision;
        fly.collision = collision;
        walk.collision = collision;
        third.collision = collision;
        if (
            !hadCollision &&
            collision &&
            sceneManager.current?.manifest.defaultMode === 'walk' &&
            viewMode === 'first' &&
            input.mode === 'fly' &&
            moveMode === 'fly'
        ) {
            setMode('walk');
            return;
        }
        hud.sync();
    }

    /** Switch to a first-person mode (fly/walk). No-op if already there. */
    function setMode(mode: 'fly' | 'walk') {
        if (viewMode === 'first' && moveMode === mode && input.mode === mode) return;
        if (mode === 'walk' && !currentCollision) {
            ui.setStatus('无碰撞数据，无法切换到行走模式');
            return;
        }
        active.onExit(state);
        state.cameraOffset.set(0, 0, 0);
        moveMode = mode;
        syncMoveMode();
        active = firstPersonController();
        viewMode = 'first';
        avatar.setVisible(false);
        avatar.clearFlightPose();
        active.onEnter(state);
        emitModeChange();
        hud.sync();
    }

    function setMoveMode(mode: 'fly' | 'walk'): void {
        if (moveMode === mode) return;
        if (mode === 'walk' && !currentCollision) {
            ui.setStatus('No collision data, cannot switch to walk mode');
            return;
        }
        active.onExit(state);
        moveMode = mode;
        syncMoveMode();
        active = viewMode === 'third' ? third : firstPersonController();
        if (viewMode === 'first') {
            avatar.setVisible(false);
            avatar.clearFlightPose();
            state.cameraOffset.set(0, 0, 0);
        } else {
            avatar.setVisible(true);
        }
        active.onEnter(state);
        if (viewMode === 'third') {
            anim.reset();
            avatar.lockRootMotion();
            rig.apply(state);
        }
        emitModeChange();
        hud.sync();
    }

    function toggleMoveMode() {
        setMoveMode(moveMode === 'walk' ? 'fly' : 'walk');
    }

    /** Toggle third-person on/off. Returns to the last first-person mode. */
    function toggleThirdPerson() {
        if (viewMode === 'third') {
            setMode(moveMode);
            ui.showHint(`第一人称 · ${moveMode === 'walk' ? '行走' : '飞行'} · V 切换`);
            return;
        }
        if (moveMode === 'walk' && !currentCollision) {
            ui.setStatus('无碰撞数据，无法切换到第三人称');
            return;
        }
        active.onExit(state);
        active = third;
        viewMode = 'third';
        syncMoveMode();
        active.onEnter(state); // shows avatar + snaps facing
        anim.reset();
        avatar.lockRootMotion();
        rig.apply(state);
        emitModeChange();
        hud.sync();
        ui.showHint(moveMode === 'fly'
            ? '第三人称飞行 · WASD/左摇杆沿镜头飞行 · E/Q 升降 · 右摇杆/鼠标控制俯仰 · 滚轮缩放'
            : '第三人称漫游 · WASD/左摇杆移动 · Shift 跑 · Space 跳 · 右摇杆/鼠标旋转视角 · 滚轮缩放');
    }

    /** Diagnostic: cycle the global gsplat debug mode to see which LOD renders. */
    const gsplatDebugCycle = [
        { mode: GSPLAT_DEBUG_LOD, label: 'LOD 着色（按 LOD 级别上色）' },
        { mode: GSPLAT_DEBUG_NODE_AABBS, label: '八叉树节点 AABB（看哪些分块已加载）' },
        { mode: GSPLAT_DEBUG_NONE, label: '关闭' }
    ];
    let gsplatDebugIdx = 2; // start off
    function cycleGsplatDebug() {
        gsplatDebugIdx = (gsplatDebugIdx + 1) % gsplatDebugCycle.length;
        const cur = gsplatDebugCycle[gsplatDebugIdx];
        app.scene.gsplat.debug = cur.mode;
        ui.showHint(`GSplat 调试：${cur.label}`);
        console.log(`[gsplat-debug] mode=${cur.mode} (${cur.label})`);
    }

    function requestRenderFrame(): void {
        renderGateApp.renderNextFrame = true;
    }

    function captureRenderState(): void {
        lastRenderState.copy(state);
        lastRenderStateReady = true;
    }

    function cameraStateChangedForRender(): boolean {
        if (!lastRenderStateReady) return true;
        const dx = state.position.x - lastRenderState.position.x;
        const dy = state.position.y - lastRenderState.position.y;
        const dz = state.position.z - lastRenderState.position.z;
        const ax = state.angles.x - lastRenderState.angles.x;
        const ay = state.angles.y - lastRenderState.angles.y;
        const az = state.angles.z - lastRenderState.angles.z;
        const ox = state.cameraOffset.x - lastRenderState.cameraOffset.x;
        const oy = state.cameraOffset.y - lastRenderState.cameraOffset.y;
        const oz = state.cameraOffset.z - lastRenderState.cameraOffset.z;
        return dx * dx + dy * dy + dz * dz > MOBILE_RENDER_POS_EPS_SQ ||
            ox * ox + oy * oy + oz * oz > MOBILE_RENDER_POS_EPS_SQ ||
            ax * ax + ay * ay + az * az > MOBILE_RENDER_ANGLE_EPS_SQ ||
            state.fov !== lastRenderState.fov;
    }

    function updateMobileRenderGate(dt: number, videoActive: boolean): void {
        if (!isMobileRuntime()) {
            requestRenderFrame();
            return;
        }

        const cameraChanged = cameraStateChangedForRender();
        if (cameraChanged || videoActive || editor.enabled) {
            mobileRenderIdleTime = 0;
            captureRenderState();
            requestRenderFrame();
            return;
        }

        mobileRenderIdleTime += dt;
        renderGateApp.renderNextFrame = mobileRenderIdleTime < MOBILE_RENDER_IDLE_SECONDS;
    }

    /** Drive a full scene transition with fade. */
    async function switchTo(
        sceneId: string,
        spawnOverride?: [number, number, number],
        anglesOverride?: [number, number]
    ) {
        if (switching) return;
        if (sceneManager.current?.manifest.id === sceneId && sceneManager.current.asset) {
            applySceneToPlayer(sceneManager.current.manifest, sceneManager.current.collision, spawnOverride, anglesOverride);
            return;
        }
        switching = true;
        ui.hidePanel();
        pointerLock.exit();
        // Show the JS-controlled loader immediately with the scene-specific text
        // (matches LOADER_TEXT_BY_SCENE in UiManager) instead of leaving a black
        // gap while fadeOut + manifest fetch run.
        eventBus.emit({ type: 'SCENE_LOAD_START', name: sceneId, progress: 0 });
        await ui.fadeOut();

        if (sceneManager.current) sceneManager.unloadScene(sceneManager.current);

        let manifest: SceneManifest;
        let instance;
        try {
            manifest = await getManifest(sceneId);
            instance = await sceneManager.loadScene(manifest);
            if (!instance.entity || !instance.asset) throw new Error('高斯场景资源未加载成功');
            if (manifest.collisionUrl && !instance.collision) {
                const collision = await loadCollision(app, manifest.collisionUrl);
                instance.objects.setBaseCollision(collision);
                instance.collision = instance.objects.collision;
            }
        } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            eventBus.emit({ type: 'LOAD_ERROR', message: `场景切换失败：${msg}` });
            await ui.fadeIn();
            switching = false;
            throw err;
        }

        ui.bindScene(manifest.hotspots, manifest.triggerZones);
        applySceneToPlayer(manifest, instance.collision, spawnOverride, anglesOverride);
        if (editor.enabled) editor.sceneChanged();

        // Preload only low-detail scene-wide coverage before fading in. The
        // normal near-detail preload runs in the background after entry.
        try {
            if (instance.entity && instance.asset) {
                await preloadGaussianScene(
                    { entity: instance.entity, asset: instance.asset },
                    state.position,
                    (frac) => eventBus.emit({ type: 'SCENE_LOAD_START', name: manifest.name, progress: frac }),
                    { mobileLowQuality: isMobileRuntime(), preloadStage: 'coarse' }
                );
            }
        } catch (err) {
            console.error('[scene] coarse gsplat preload failed:', err);
            if (isMobileRuntime()) {
                ui.hideLoader();
                eventBus.emit({
                    type: 'LOAD_ERROR',
                    message: `低清高斯场景加载超时，已停止进入场景以避免黑屏。${err instanceof Error ? err.message : String(err)}`
                });
                await ui.fadeIn();
                switching = false;
                throw err;
            }
        }
        // Now the first view is resident — signal ready (sets bar to 100% and
        // lets the loader hide).
        eventBus.emit({ type: 'SCENE_READY', name: manifest.name });

        // Phase 4: log live asset count so memory unload can be verified by hand.
        console.log(`[scene] "${manifest.name}" ready — live assets: ${sceneManager.assetCount()}`);

        await ui.fadeIn();
        switching = false;
        // 场景就绪后才显示 HUD、FPS 与 ⚙ 设置按钮（登录/加载阶段全部隐藏）。
        hud.setVisible(false);
        fpsMeter?.classList.remove('hidden');
        worldUI.revealSettingsButton();
        // Mobile: the desktop 'full' background preload (near LOD0 + coarsest
        // for every node) OOM-killed large scenes (meihuashan 781MB/125
        // chunks), so mobile never runs it. Instead a bounded 'near' preload
        // sharpens just the spawn area a moment after entry: a handful of
        // LOD3 chunks within MOBILE_NEAR_PRELOAD_RADIUS_M meters. Beyond that
        // radius the GSplat streaming system loads chunks on demand.
        const backgroundPreload = instance.entity && instance.asset && !isMobileRuntime();
        if (backgroundPreload) {
            const entity = instance.entity!;
            const asset = instance.asset!;
            window.setTimeout(() => {
                void preloadGaussianScene(
                    { entity, asset },
                    state.position,
                    undefined,
                    { mobileLowQuality: false, preloadStage: 'full' }
                ).catch((err) => {
                    console.error('[scene] background gsplat preload failed:', err);
                });
            }, 0);
        }
        if (isMobileRuntime()) {
            window.setTimeout(() => sceneManager.loadDeferredMobileAssets(instance, installSceneCollision), 500);
            if (instance.entity && instance.asset) {
                const entity = instance.entity;
                const asset = instance.asset;
                window.setTimeout(() => {
                    if (sceneManager.current !== instance) return;
                    console.info('[scene] mobile starts background near gsplat preload');
                    void preloadGaussianScene(
                        { entity, asset },
                        state.position,
                        undefined,
                        {
                            mobileLowQuality: true,
                            preloadStage: 'near',
                            nearLod: MOBILE_NEAR_PRELOAD_LOD,
                            nearRadius: MOBILE_NEAR_PRELOAD_RADIUS_M
                        }
                    ).catch((err) => {
                        console.error('[scene] mobile near gsplat preload failed:', err);
                    });
                }, MOBILE_NEAR_PRELOAD_DELAY_MS);
            }
        }
        ui.showHint(`已进入「${manifest.name}」· WASD 移动 · V 切换模式`);
    }

    // --- Portal → scene switch (honors requireConfirm) ---
    eventBus.on('PORTAL_ENTER', async (e) => {
        if (!e.id || (!session.online && !solo)) return;
        const obj = e.id ? sceneManager.current?.objects.get(e.id) : undefined;
        const portal = obj?.portal;
        if (portal?.requireConfirm) {
            const ok = await ui.showConfirm(portal.confirmText ?? `传送至「${e.targetScene}」？`);
            if (!ok) return;
        }
        if (session.online) {
            await session.transfer(e.id);
        } else if (e.targetScene) {
            // 离线单人漫游：直接本地切换到传送门目标场景。
            await switchTo(e.targetScene, e.targetSpawn, e.targetAngles ? [e.targetAngles[0], e.targetAngles[1]] : undefined);
        }
    });

    // --- Object event action: switchScene / hideScene (other action types handled by UiManager) ---
    eventBus.on('OBJECT_TRIGGER', (e) => {
        const actions = e.actions ?? (e.action ? [e.action] : []);
        for (const a of actions) {
            if (a.type === 'switchScene' && a.payload.targetScene) {
                if (session.online) {
                    if (e.id) void session.transfer(e.id);
                } else if (solo) {
                    // 离线单人漫游：本地切换目标场景。
                    void switchTo(a.payload.targetScene, a.payload.targetSpawn, a.payload.targetAngles ? [a.payload.targetAngles[0], a.payload.targetAngles[1]] : undefined);
                }
            } else if (a.type === 'hideScene') {
                sceneManager.hideSceneContent();
            }
        }
    });

    // --- hideScene zone exit → restore hidden scene content ---
    eventBus.on('SCENE_VISIBILITY', (e) => {
        if (e.visible) sceneManager.restoreSceneContent();
    });

    // --- Dismiss error screen on click (allows continued exploration) ---
    document.getElementById('error-screen')!.addEventListener('click', () => ui.hideError());

    // --- Click/tap: lock pointer on desktop, interact directly on mobile taps ---
    let touchTapStart: { pointerId: number; x: number; y: number; t: number } | null = null;
    canvas.addEventListener('pointerdown', (e) => {
        if (switching || editor.enabled) return;
        if (e.pointerType !== 'touch') return;
        touchTapStart = { pointerId: e.pointerId, x: e.clientX, y: e.clientY, t: performance.now() };
    });
    canvas.addEventListener('pointerup', (e) => {
        if (!touchTapStart || e.pointerId !== touchTapStart.pointerId) return;
        const start = touchTapStart;
        touchTapStart = null;
        if (switching || editor.enabled) return;
        const moved = Math.hypot(e.clientX - start.x, e.clientY - start.y);
        const elapsed = performance.now() - start.t;
        if (moved > TOUCH_TAP_MOVE_THRESHOLD || elapsed > TOUCH_TAP_MAX_MS) return;
        if (isMobileRuntime() && e.pointerType === 'touch') {
            _mouseX = e.clientX;
            _mouseY = e.clientY;
            _mouseValid = true;
            triggerSceneClickInteraction();
        }
    });
    canvas.addEventListener('pointercancel', () => { touchTapStart = null; });

    canvas.addEventListener('click', () => {
        if (switching) return;
        if (editor.enabled) return; // editor owns clicks while active (cursor-driven)
        if (isMobileRuntime()) return;
        triggerSceneClickInteraction();
    });

    // --- Editor mouse: cursor aim + left-drag look + left-click place/select + wheel depth ---
    // Left-drag (hold + move beyond threshold) rotates the camera; a left-click
    // (down+up without dragging) places (if a prop is active) or selects.
    // While a gizmo handle is being dragged, the gizmo owns the pointer — skip
    // look/place/select so the camera stays put and the drag commits cleanly.
    let leftDown = false;
    let rightDown = false;
    let downX = 0;
    let downY = 0;
    let dragged = false;
    const DRAG_THRESHOLD = 4;
    const look = (e: MouseEvent) =>
        input.frame.appendRotate(
            e.movementX * input.mouseSensitivity,
            e.movementY * input.mouseSensitivity,
            0
        );
    canvas.addEventListener('mousemove', (e) => {
        if (!editor.enabled) return;
        editor.setMouse(e.clientX, e.clientY);
        if (editor.isGizmoDragging) return;
        if (leftDown) {
            if (!dragged && Math.hypot(e.clientX - downX, e.clientY - downY) > DRAG_THRESHOLD) dragged = true;
            if (dragged) look(e);
        } else if (rightDown) {
            look(e);
        }
    });
    canvas.addEventListener('mousedown', (e) => {
        if (!editor.enabled) return;
        if (editor.isGizmoDragging) return; // gizmo grabbed a handle on pointerdown
        if (e.button === 0) { leftDown = true; downX = e.clientX; downY = e.clientY; dragged = false; }
        else if (e.button === 2) rightDown = true;
    });
    window.addEventListener('mouseup', (e) => {
        if (!editor.enabled) return;
        if (e.button === 0) {
            if (leftDown && !dragged && !editor.isGizmoDragging) editor.onLeftClick();
            leftDown = false;
        } else if (e.button === 2) rightDown = false;
    });
    canvas.addEventListener('wheel', (e) => {
        if (editor.enabled) {
            editor.onWheel(e.deltaY);
            e.preventDefault();
        } else if (viewMode === 'third') {
            // Scroll up = closer, down = farther.
            third.adjustDistance(-e.deltaY * 0.002);
            e.preventDefault();
        }
    }, { passive: false });

    // --- Keyboard: mode toggle / reset / interact / close / editor ---
    window.addEventListener('keydown', (e) => {
        if (isEditableTarget(e.target)) return;
        // Editor shortcuts. Text inputs own their keystrokes while focused.
        if (e.code === 'F2') {
            if (editor.enabled) exitEditor(); else enterEditor();
            return;
        }
        if (editor.enabled) {
            // Ctrl-key combos first (undo/redo/duplicate).
            if (e.ctrlKey || e.metaKey) {
                switch (e.code) {
                    case 'KeyZ': e.preventDefault(); editor.undo(); return;
                    case 'KeyY': e.preventDefault(); editor.redo(); return;
                    case 'KeyD': e.preventDefault(); editor.duplicate(); return;
                }
            }
            switch (e.code) {
                case 'KeyW': editor.state.setMode('move'); return;
                case 'KeyE': editor.state.setMode('rotate'); return;
                case 'KeyR': editor.state.setMode('scale'); return;
                case 'KeyQ': editor.state.setMode('select'); return;
                case 'Delete':
                    if (editor.selectedId) { e.preventDefault(); editor.deleteSelected(); }
                    return;
                case 'Escape':
                    if (editor.placing) { editor.cancel(); return; }
                    exitEditor();
                    return;
            }
        }
        if (switching) return;
        switch (e.code) {
            case 'F8':
                // Diagnostic: cycle gsplat debug mode (LOD colorize → octree
                // node AABBs → off) to see which LOD is actually rendering.
                cycleGsplatDebug();
                break;
            case 'KeyV':
                toggleMoveMode();
                break;
            case 'KeyR':
                void requestReset();
                break;
            case 'KeyE':
                if (pointerLock.locked) {
                    sceneManager.current?.hotspots.onClick(state);
                    sceneManager.current?.objects.onPressE(state);
                }
                break;
            case 'Escape':
                ui.hidePanel();
                break;
        }
    });

    // --- Main update loop (gated during switches) ---
    const playerPos = new Vec3();
    const lastSentPosition = new Vec3();
    let sendElapsed = 0;
    let resetPending = false;
    async function requestReset() {
        if (resetPending || !session.online) return;
        resetPending = true;
        try { await session.reset(); } catch (e) { ui.showHint(String(e)); } finally { resetPending = false; }
    }
    walk.onNetworkReset = third.onNetworkReset = () => { void requestReset(); };
    const applyReset = (pose: PlayerState) => {
        state.position.set(...pose.position); active.onEnter(state); anim.reset(); rig.apply(state); lastSentPosition.copy(state.position); sendElapsed = 0;
    };
    session.onReset = applyReset;
    app.on('update', (dt: number) => {
        if (switching || (!session.online && !solo) || resetPending) {
            input.frame.read();
            remotes.update(session.serverTime);
            requestRenderFrame();
            return;
        }
        fpsAccum += dt;
        fpsFrames++;
        if (fpsMeter && fpsAccum >= 0.5) {
            fpsMeter.textContent = `${Math.round(fpsFrames / fpsAccum)} FPS`;
            fpsAccum = 0;
            fpsFrames = 0;
        }
        input.update(dt);
        active.update(dt, input.frame, state);
        rig.apply(state);
        sendElapsed += dt;
        if (viewMode === 'first') {
            avatar.snapFacing(state.angles.y + 180);
            const speed = state.position.distance(lastSentPosition) / Math.max(0.001, sendElapsed);
            anim.update(dt, {speed, grounded: moveMode === 'fly' || walk.networkGrounded, jumping: moveMode === 'walk' && walk.networkJumping, running: walk.networkRunning || speed > 3});
        }
        if (sendElapsed >= 0.05) {
            const elapsed = Math.max(0.05, sendElapsed);
            const velocity: [number, number, number] = [(state.position.x-lastSentPosition.x)/elapsed,(state.position.y-lastSentPosition.y)/elapsed,(state.position.z-lastSentPosition.z)/elapsed];
            const speed = Math.hypot(...velocity), controller = viewMode === 'third' ? third : walk;
            const grounded = moveMode === 'walk' ? controller.networkGrounded : false;
            const rotation = rig.avatarRoot.getRotation();
            session.publish({position:[state.position.x,state.position.y,state.position.z],rotation:[rotation.x,rotation.y,rotation.z,rotation.w],velocity,
                locomotion: classifyLocomotion(speed, moveMode, grounded, controller.networkRunning), grounded});
            lastSentPosition.copy(state.position); sendElapsed = 0;
        }
        if (editor.enabled) editor.update(state);
        updateNetworkAction();
        hud.update();

        const inst = sceneManager.current;
        let sceneActivity = false;
        if (inst) {
            playerPos.copy(state.position);
            inst.hotspots.update(playerPos);
            inst.zones.update(playerPos);
            inst.portals.update(playerPos);
            sceneActivity = inst.objects.update(state, dt);
        }
        updateMobileRenderGate(dt, remotes.update(session.serverTime) || sceneActivity);
    });

    // --- Boot: load the registry's entry scene ---
    ui.setStatus('正在加载场景……');
    let loadQueue: Promise<void> = Promise.resolve();
    let avatarKey = '';
    /** Rebuild the local avatar model (scene join + in-session avatar switch). */
    async function applyAvatar(av: Avatar): Promise<void> {
        const key = av.id + ':' + av.revision;
        if (key === avatarKey) return;
        avatarKey = key;
        avatar.destroy();
        avatar = new AvatarController(app, rig, modelConfig(av));
        avatar.setVisible(viewMode === 'third');
        await avatar.load();
        anim = new AnimationStateMachine(avatar);
        third.avatar = avatar;
        third.anim = anim;
        hud.setActions(modelConfig(av).actionAnims);
    }
    session.onLoad = (admission: Admission) => {
        const work = loadQueue.catch(() => {}).then(async () => {
            remotes.clear();
            const manifest = {...admission.scene.manifest, hotspots: admission.scene.manifest.hotspots ?? [], triggerZones: admission.scene.manifest.triggerZones ?? [], portals: admission.scene.manifest.portals ?? [], defaultMode: admission.scene.manifest.defaultMode ?? 'fly', fallLimit: admission.scene.manifest.fallLimit ?? -100} as SceneManifest;
            manifestCache.set(admission.scene.id, manifest);
            await applyAvatar(admission.avatar);
            await switchTo(admission.scene.id, admission.spawn, [admission.angles[0], admission.angles[1]]);
            lastSentPosition.copy(state.position); sendElapsed = 0;
        });
        loadQueue = work; return work;
    };
    window.addEventListener('pagehide', () => { session.dispose(); remotes.destroy(); avatar.destroy(); }, {once:true});
    try {
        if (solo) {
            // 世界场景（离线单人漫游）：不连接房间。场景 manifest 直接读静态
            // scene.json（spawn/碰撞/传送门等全部可用），形象取用户当前佩戴的。
            const work = loadQueue.catch(() => {}).then(async () => {
                try {
                    const avatars = await business.api<{ id: string; name: string; modelUrl: string; config: Record<string, unknown> }[]>('/hub/avatars');
                    const mine = avatars.find((a) => a.id === business.user.avatarId) ?? avatars[0];
                    if (mine) await applyAvatar({ ...mine, revision: 1, enabled: true } as Avatar);
                } catch { /* 形象拉取失败时沿用默认 avatarConfig 模型 */ }
                await switchTo(business.invite.sceneId);
                lastSentPosition.copy(state.position); sendElapsed = 0;
            });
            loadQueue = work;
            await work;
        } else {
            await session.start();
        }
    } catch (err) {
        // 满员是业务态而非初始化故障：toast 5 秒后自动回首页（entry 会重新进默认场景）。
        if (err instanceof Error && err.message === 'ROOM_FULL') {
            ui.showHint('房间人数已满', 5000);
            window.setTimeout(() => location.assign('/'), 5500);
            return;
        }
        throw err;
    }
    // HUD/FPS/设置按钮在 switchTo 场景就绪后统一显示，这里不再提前显示。
    ui.showHint('按住左键旋转视角 · WASD 移动 · V 切换行走/飞行');
}

main().catch((err) => {
    console.error(err);
    const el = document.getElementById('error-message');
    if (el) el.textContent = `初始化失败：${err instanceof Error ? err.message : String(err)}`;
    document.getElementById('error-screen')?.classList.remove('hidden');
});
